import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faUserGraduate,
  faTriangleExclamation,
  faUserSlash,
  faGlobe,
  faCakeCandles,
  faSchool,
} from '@fortawesome/free-solid-svg-icons';
import 'flag-icons/css/flag-icons.min.css';
import { countryOf, cityOf, offsetLabel, localTimeIn, ianaOf } from '../Admin/Studentzone';
import StudentClasses from './StudentClasses';
import '../Admin/StudentProfile.css'; // same look as the admin student profile ("rmasp-")
import './StudentProfile.css';

const API = process.env.REACT_APP_API_BASE_URL;

/* Teacher › student profile: the admin profile's header (photo, level, local time, age, grade),
   then the student's classes (progress, sessions, courses). No parent contact, no payments. */
const Photo = ({ src, flag, flagTitle }) => {
  const [broken, setBroken] = useState(false);
  return (
    <span className="rmasp-photo-wrap">
      <span className="rmasp-photo">
        {src && !broken ? <img src={src} alt="" onError={() => setBroken(true)} /> : <FontAwesomeIcon icon={faUserGraduate} />}
      </span>
      {flag && (
        <span className="rmasp-flag" title={flagTitle}>
          <span className={`fi fi-${flag}`} />
        </span>
      )}
    </span>
  );
};

/* the student's clock, ticking every 30 seconds */
const StudentClock = ({ tz, hours }) => {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(id);
  }, []);
  return <>{localTimeIn(tz, hours, now) || '—'}</>;
};

const StudentProfile = () => {
  const { studentId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reload, setReload] = useState(0);

  // ---------- Load ----------
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    (async () => {
      try {
        const res = await axios.get(`${API}/api/teacher/studentprofile/${studentId}`);
        if (!cancelled) setData(res.data || null);
      } catch (err) {
        console.error('Error fetching student profile:', err);
        if (!cancelled) setError(err.response?.status === 404 ? '' : 'We couldn’t load this student’s profile.');
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [studentId, reload]);

  const back = (
    <Link to="/teacher/MyStudents" className="rmasp-back">
      <FontAwesomeIcon icon={faArrowLeft} /> My Students
    </Link>
  );

  if (loading) {
    return (
      <div className="rmasp rmtsp-page" aria-busy="true">
        <div className="rmasp-inner">
          <div className="rmtsp-skel rmtsp-skel--back" />
          <div className="rmtsp-skel rmtsp-skel--hero" />
          <div className="rmtsp-skel rmtsp-skel--body" />
        </div>
      </div>
    );
  }

  const student = data?.student;
  if (error || !student) {
    return (
      <div className="rmasp rmtsp-page">
        <div className="rmasp-inner">
          {back}
          <div className="rmasp-empty">
            <FontAwesomeIcon icon={error ? faTriangleExclamation : faUserSlash} className={error ? 'is-error' : ''} />
            <p>{error || 'This profile doesn’t exist or is no longer linked to you.'}</p>
            {error && (
              <button type="button" className="rmasp-btn" onClick={() => setReload((n) => n + 1)}>
                Try again
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  const name = [student.FirstName, student.LastName].filter(Boolean).join(' ') || 'Student';
  const tzName = ianaOf(student.Timezone || '');
  const tzHours = student.TimeHours;
  const flag = countryOf(student, { Timezone: student.Timezone });
  const tzOffset = offsetLabel(tzName, tzHours);
  const hasZone = !!(tzName || tzOffset);
  const isDemo = Number(student.Type) === 1;
  const status = student.SubscriptionStatus || 'active';

  return (
    <div className={`rmasp rmtsp-page ${isDemo ? 'is-demo-profile' : 'is-paid-profile'}`}>
      <div className="rmasp-inner">
        {back}

        {/* ---------- Header ---------- */}
        <header className="rmasp-hero">
          <span className="rmasp-kind" aria-hidden="true">{isDemo ? 'Demo student' : 'Paid student'}</span>
          <Photo src={student.Image} flag={flag} flagTitle={tzName} />
          <div className="rmasp-who">
            <span className="rmasp-eyebrow">{isDemo ? 'Demo student' : 'Student'}</span>
            <h1>{name}</h1>
            <div className="rmasp-tags">
              <span className={isDemo ? 'is-warn' : 'is-good'}>{isDemo ? 'Demo' : 'Paid'}</span>
              <span className="is-info">{Number(student.IdGradesPlans) === 1 ? 'Little' : 'Junior'}</span>
              {status === 'paused' && <span className="is-warn">Paused</span>}
              {status === 'stopped' && <span className="is-off">Stopped</span>}
              {status === 'pending' && <span className="is-warn">Pending</span>}
            </div>
          </div>
          <dl className="rmasp-seen">
            <div className="rmasp-zone">
              <dt>
                <FontAwesomeIcon icon={faGlobe} /> Local time
              </dt>
              <dd>{hasZone ? <StudentClock tz={tzName} hours={tzHours} /> : 'No time zone'}</dd>
              {hasZone && (
                <small>
                  {flag && <span className={`fi fi-${flag}`} />} {cityOf(tzName) || 'Time zone'}
                  {tzOffset ? ` · ${tzOffset}` : ''}
                </small>
              )}
            </div>
            {student.Age != null && (
              <div>
                <dt>
                  <FontAwesomeIcon icon={faCakeCandles} /> Age
                </dt>
                <dd>{Number(student.Age) >= 19 ? 'Over 18' : student.Age}</dd>
              </div>
            )}
            {student.Grade != null && student.Grade !== '' && (
              <div>
                <dt>
                  <FontAwesomeIcon icon={faSchool} /> Grade
                </dt>
                <dd>{student.Grade}</dd>
              </div>
            )}
          </dl>
        </header>

        {/* ---------- Classes ---------- */}
        <StudentClasses StudentId={studentId} />
      </div>
    </div>
  );
};

export default StudentProfile;
